import { useLang } from "@/i18n";
import { IMG } from "@/data/media";
import { PageHero } from "@/components/PageHero";
import { FadeUp } from "@/components/motion";
import { Marquee } from "@/components/Marquee";
import { SupportCta } from "@/components/SupportCta";

export default function About() {
  const { t } = useLang();
  const a = t.about;
  return (
    <>
      <PageHero eyebrow={a.eyebrow} lines={a.titleLines} intro={a.intro} image={IMG.childCap} imageAlt={t.gallery.captions.childCap} corner="bl" accent="var(--yellow)" />
      <section className="py-20 sm:py-28 bg-white" data-testid="about-motto">
        <div className="wrap">
          <FadeUp as="p" className="font-display text-4xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-5xl">
            <span className="italic text-[var(--green-deep)]">“</span>{a.motto}<span className="italic text-[var(--green-deep)]">”</span>
          </FadeUp>
        </div>
      </section>
      <section className="py-20 sm:py-28" data-testid="about-story">
        <div className="wrap grid gap-12 lg:grid-cols-12 items-center">
          <FadeUp className="lg:col-span-5">
            <div className="relative aspect-square max-w-md overflow-hidden petal-tr bg-[var(--coral-pale)] grid place-items-center">
              <img src={IMG.emblem} alt={a.storyTitle} loading="lazy" className="w-[78%] h-auto rounded-full" />
            </div>
          </FadeUp>
          <div className="lg:col-span-7">
            <span className="eyebrow">{a.storyTitle}</span>
            <FadeUp as="p" className="mt-6 text-2xl sm:text-3xl font-display leading-snug" data-testid="about-story-text">{a.story}</FadeUp>
          </div>
        </div>
      </section>
      <section className="py-20 sm:py-28 bg-white">
        <div className="wrap grid gap-5 md:grid-cols-2">
          {[[a.missionTitle, a.mission, "bg-[var(--green-pale)]", "rounded-[2.5rem_2.5rem_2.5rem_0.5rem]"], [a.visionTitle, a.vision, "bg-[var(--blue-pale)]", "rounded-[2.5rem_2.5rem_0.5rem_2.5rem]"]].map(([title, text, tone, r], i) => (
            <FadeUp key={title} delay={i * 0.1} className={`p-8 sm:p-12 ${tone} ${r}`} data-testid={`about-card-${i}`}>
              <h2 className="text-4xl sm:text-5xl">{title}</h2>
              <p className="mt-6 text-lg leading-relaxed text-[var(--ink-soft)]">{text}</p>
            </FadeUp>
          ))}
        </div>
      </section>
      <section className="py-20 sm:py-28" data-testid="about-values">
        <div className="wrap">
          <span className="eyebrow">{a.valuesTitle}</span>
          <div className="mt-10 grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
            {a.values.map(([title, text], i) => (
              <FadeUp key={title} delay={i * 0.08} className="border-t-2 border-[var(--ink)] pt-6">
                <span className="text-sm font-semibold text-[var(--green-deep)]">0{i + 1}</span>
                <h3 className="mt-3 text-3xl">{title}</h3>
                <p className="mt-3 text-[var(--ink-soft)] leading-relaxed">{text}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>
      <Marquee words={t.home.marquee} tone="dark" />
      <section className="py-20 sm:py-28">
        <div className="wrap grid gap-10 lg:grid-cols-12 items-center">
          <FadeUp className="lg:col-span-7">
            <h2 className="text-4xl sm:text-5xl">{a.italyTitle}</h2>
            <p className="mt-6 text-lg leading-relaxed text-[var(--ink-soft)] max-w-2xl">{a.italyText}</p>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-5 aspect-[4/3] overflow-hidden rounded-[2rem] img-zoom">
            <img src={IMG.teamShirts} alt={t.gallery.captions.teamShirts} loading="lazy" className="photo w-full h-full object-cover" />
          </FadeUp>
        </div>
      </section>
      <SupportCta />
    </>
  );
}
